/**
 * NEXORA AI - StepHeader Component (Design System)
 * Epic 1.3 - Onboarding Premium
 * 
 * Composant réutilisable pour les headers de steps/sections
 * Utilisé dans : Onboarding, Assistants IA, Formulaires multi-étapes, Workflows
 * 
 * API Extensible :
 * - Icône/Illustration optionnelle
 * - Titre + Subtitle
 * - Indicateur de progression secondaire (future)
 * - Aide contextuelle / Info button (future)
 * - Animation d'entrée configurable
 */

import React, { useState } from 'react';
import { Info, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * StepHeader Props
 * 
 * @param {ReactNode} icon - Icône Lucide ou emoji
 * @param {string} iconBg - Gradient background pour l'icône
 * @param {string} iconBorder - Couleur de bordure pour l'icône
 * @param {string} title - Titre principal (H2)
 * @param {string} subtitle - Sous-titre explicatif
 * @param {string} helper - Texte d'aide additionnel (optionnel)
 * @param {ReactNode} badge - Badge/Tag personnalisé (optionnel)
 * @param {boolean} showProgress - Afficher indicateur progression secondaire (future)
 * @param {number} progress - Pourcentage de progression (0-100)
 * @param {boolean} showHelp - Afficher bouton d'aide contextuelle (future)
 * @param {string} helpText - Texte de l'aide contextuelle
 * @param {ReactNode} illustration - Illustration optionnelle (future, SVG/Image)
 * @param {string} align - Alignement : 'left' | 'center' | 'right'
 * @param {boolean} animate - Activer animations d'entrée
 * @param {string} className - Classes supplémentaires
 */
export default function StepHeader({
  icon,
  iconBg = 'from-blue-600/20 to-purple-600/20',
  iconBorder = 'border-blue-500/30',
  title,
  subtitle = null,
  helper = null,
  badge = null,
  showProgress = false,
  progress = 0,
  showHelp = false,
  helpText = '',
  illustration = null,
  align = 'center',
  animate = true,
  className = '',
}) {
  const [helpOpen, setHelpOpen] = useState(false);

  // Classes d'alignement
  const alignClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  const MotionWrapper = animate ? motion.div : 'div';
  const motionProps = animate
    ? {
        variants: containerVariants,
        initial: 'hidden',
        animate: 'visible',
      }
    : {};

  return (
    <MotionWrapper
      {...motionProps}
      className={cn('mb-8 sm:mb-10 flex flex-col', alignClasses[align], className)}
    >
      {/* Illustration (future feature) */}
      {illustration && (
        <motion.div
          variants={animate ? itemVariants : {}}
          className="mb-6 sm:mb-8"
        >
          {illustration}
        </motion.div>
      )}

      {/* Icône */}
      {icon && (
        <motion.div
          variants={animate ? iconVariants : {}}
          className={cn(
            'inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20',
            'rounded-2xl bg-gradient-to-br border mb-6',
            iconBg,
            iconBorder
          )}
        >
          {typeof icon === 'string' ? (
            <span className="text-4xl sm:text-5xl">{icon}</span>
          ) : (
            React.cloneElement(icon, {
              className: cn(
                'w-8 h-8 sm:w-10 sm:h-10',
                icon.props.className
              ),
              strokeWidth: icon.props.strokeWidth || 1.5,
            })
          )}
        </motion.div>
      )}

      {/* Titre + Badge */}
      <motion.div
        variants={animate ? itemVariants : {}}
        className="flex items-center gap-3 mb-3 sm:mb-4"
      >
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white">
          {title}
        </h2>
        {badge && (
          <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm font-medium">
            {badge}
          </span>
        )}
      </motion.div>

      {/* Subtitle */}
      {subtitle && (
        <motion.p
          variants={animate ? itemVariants : {}}
          className={cn(
            'text-base md:text-lg text-zinc-400 mb-2',
            align === 'center' ? 'max-w-2xl' : 'max-w-xl'
          )}
        >
          {subtitle}
        </motion.p>
      )}

      {/* Helper + Aide contextuelle */}
      {(helper || showHelp) && (
        <motion.div
          variants={animate ? itemVariants : {}}
          className="flex items-center gap-2 text-sm text-zinc-500 mt-4"
        >
          {helper && (
            <div className="flex items-center gap-1.5">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>{helper}</span>
            </div>
          )}
          
          {/* Bouton d'aide contextuelle (future feature) */}
          {showHelp && (
            <button
              onClick={() => setHelpOpen(!helpOpen)}
              className="ml-2 p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
              aria-label="Aide contextuelle"
            >
              <HelpCircle className="w-4 h-4 text-blue-400" />
            </button>
          )}
        </motion.div>
      )}

      {/* Panneau d'aide contextuelle (future feature) */}
      <AnimatePresence>
        {helpOpen && helpText && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-sm text-blue-300"
          >
            {helpText}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Indicateur de progression secondaire (future feature) */}
      {showProgress && (
        <motion.div
          variants={animate ? itemVariants : {}}
          className="mt-4 w-full max-w-md"
        >
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2 text-center">
            {progress}% complété
          </p>
        </motion.div>
      )}
    </MotionWrapper>
  );
}
